import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { CarouselSlider } from '../../models/carousel-slider';
import { SystemApp } from '../../constans/SystemConfigurations';
import { LoadingMaskServiceService } from '../loading-frame/loading-frame.component';
import { CarouselSliderService } from '../../services/carouselSlider.service';
import type {SwiperOptions} from 'swiper/types';
import {AdvertisementComponent, AdvertisementEnum} from '../../core/advertisement/advertisement.component';
import {AppModule} from "../../app.module";
import {JobCirclesBannerComponent} from "../required/required.component";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {RouterLink} from "@angular/router";
import {SwiperElementDirective} from '../../shared/swiper-element/swiper-element.directive';


@Component({
    selector: 'app-carousel-slider',
    templateUrl: './carousel-slider.component.html',
    styleUrls: ['./carousel-slider.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    SwiperElementDirective,
    MatIconModule,
    MatButtonModule,
    AdvertisementComponent,
    JobCirclesBannerComponent,
    RouterLink
],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CarouselSliderComponent implements OnInit, OnDestroy {

  // ── Public state ──────────────────────────────────────────────
  carouselSlider: CarouselSlider[] = [];
  serverUrl: string = SystemApp.base_url_api;

  readonly swiperConfig: SwiperOptions = this.buildSwiperConfig();

  // Re-export enum so the template can reference it
  readonly AdvertisementEnum = AdvertisementEnum;

  // Starts hidden (see .extended-banner.is-hidden in the stylesheet) so the
  // wrapper's own min-height/margin never shows as a blank gap before we
  // know — or when there simply is no — extend banner to show.
  hasExtendBanner = false;

  // ── Private ───────────────────────────────────────────────────
  private readonly destroy$ = new Subject<void>();

  // ─────────────────────────────────────────────────────────────
  constructor(
      private readonly carouselSliderService: CarouselSliderService,
      private readonly loadingMask: LoadingMaskServiceService,
      private readonly cdr: ChangeDetectorRef,
  ) {}

  // ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadSlides();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onExtendBannerReady(hasAd: boolean): void {
    this.hasExtendBanner = hasAd;
    this.cdr.markForCheck();
  }

  // ── Private helpers ───────────────────────────────────────────

  private loadSlides(): void {
    this.loadingMask.setOnLoadingMask(true, true);

    this.carouselSliderService
        .getCarouselSlider()
        .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.loadingMask.setOnLoadingMask(false, false);
              this.cdr.markForCheck();
            }),
        )
        .subscribe({
          next: (res) => {
            this.carouselSlider = res.CarouselSlider ?? [];
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('[CarouselSlider] Failed to load slides:', err);
          },
        });
  }

  private buildSwiperConfig(): SwiperOptions {
    const pagination = {
      el: '.swiper-pagination',
      clickable: true,
    };

    return {
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      spaceBetween: 0,
      keyboard: true,
      navigation: {
        prevEl: '.slider-nav--prev',
        nextEl: '.slider-nav--next',
      },
      pagination,
      grabCursor: true,
      loop: false,
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
      },
      speed: 600,
      effect: 'fade',   // smoother than "slide" for banner carousels
    };
  }
}
